import { useEffect, useState, type ReactNode } from "react";
import { RealmThemeProvider } from "@chuzi/shared/ui";
import { setRealmLexiconOverrides } from "@chuzi/shared/config";
import type { LocaleId, RealmId, UserProfile } from "@chuzi/shared/types";
import { api } from "./api";
import { auth } from "./auth";

/**
 * Wraps the app in a `RealmThemeProvider` keyed off the authenticated user's
 * realm + locale preferences. On boot, if a token is present, we fetch the
 * user profile and feed realm/locale into the theme provider; otherwise we
 * render with `realmId={null}` so chuzi-shared/ui falls back to the neutral
 * lexicon and default theme tokens until login.
 *
 * Stale token → fail silently and clear it. The user lands on /login on the
 * next protected route guard.
 */
export function RealmProvider({ children }: { children: ReactNode }) {
  const [realmId, setRealmId] = useState<RealmId | null>(null);
  const [locale, setLocale] = useState<LocaleId | null>(null);
  const [lexiconRevision, setLexiconRevision] = useState(0);

  useEffect(() => {
    if (!auth.isAuthenticated()) return;
    let cancelled = false;
    api.auth
      .user()
      .then((user: UserProfile) => {
        if (cancelled) return;
        // Backend can transiently return null realm during startup hydration.
        // Keep web viewport visuals/tokens aligned by defaulting to cosmos.
        setRealmId(user.realm === "wilds" ? "wilds" : "cosmos");
        setLocale(user.locale);
      })
      .catch(() => {
        auth.clearToken();
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    function onOverride() {
      setLexiconRevision((v) => v + 1);
    }
    window.addEventListener("chuzi:realm-lexicon-overrides", onOverride);
    return () =>
      window.removeEventListener("chuzi:realm-lexicon-overrides", onOverride);
  }, []);

  return (
    <RealmThemeProvider
      realmId={realmId}
      locale={locale}
      lexiconRevision={lexiconRevision}
    >
      {children}
    </RealmThemeProvider>
  );
}

export function applyRuntimeRealmLexiconOverride(
  realmId: RealmId,
  values: Record<string, string>,
) {
  const existing = (() => {
    try {
      const raw = localStorage.getItem("chuzi.realmLexiconOverrides");
      return raw
        ? (JSON.parse(raw) as Partial<Record<RealmId, Record<string, string>>>)
        : {};
    } catch {
      return {};
    }
  })();
  const merged: Partial<Record<RealmId, Record<string, string>>> = {
    ...existing,
    [realmId]: {
      ...(existing[realmId] ?? {}),
      ...values,
    },
  };
  localStorage.setItem("chuzi.realmLexiconOverrides", JSON.stringify(merged));
  setRealmLexiconOverrides(merged);
  window.dispatchEvent(new Event("chuzi:realm-lexicon-overrides"));
}

const DEPRECATED_OVERRIDE_KEYS = ["editor_alt_media"];

export function loadRuntimeRealmLexiconOverrides() {
  try {
    const raw = localStorage.getItem("chuzi.realmLexiconOverrides");
    const parsed = raw
      ? (JSON.parse(raw) as Partial<Record<RealmId, Record<string, string>>>)
      : {};

    let dirty = false;
    for (const realmId of Object.keys(parsed) as RealmId[]) {
      const overrides = parsed[realmId];
      if (!overrides) continue;
      for (const key of DEPRECATED_OVERRIDE_KEYS) {
        if (key in overrides) {
          delete overrides[key];
          dirty = true;
        }
      }
    }
    if (dirty) {
      localStorage.setItem(
        "chuzi.realmLexiconOverrides",
        JSON.stringify(parsed),
      );
    }

    setRealmLexiconOverrides(parsed);
  } catch {
    setRealmLexiconOverrides({});
  }
}
